'use client';

import { PromotionStar } from '@ahafood/icons';
import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import { Image } from '@ahafood/ui/components/image';
import { Separator } from '@ahafood/ui/components/separator';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, JSFunction } from '@ahm/common-helpers';
import { MapPin, Facebook, ChevronRight, Star } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { StoreDetailSheet } from './store-detail-sheet';
import type { Rating, Store } from '@/api/ahafood/get-store-fn';
import { useRatingSheet } from '@/app/[locale]/cua-hang/components/rating/rating-sheet-context';
import { calculateETA, formatDistance } from '@/lib/eta-calculator';
import { useIsMobile } from '@/lib/is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { useAiFoodOrderStore } from '@/store';

interface StoreHeaderProps {
  isOpen?: boolean;
  store: Store;
  onOpenFacebook: (pageId: number) => void;
  onSendMessage: (pageId: number) => void;
  isMobileView?: boolean;
}

export function ETABadge({
  cityId,
  distance,
}: {
  cityId: string | null;
  distance: number;
}) {
  const etaMinutes = calculateETA(cityId, distance);
  if (etaMinutes === null) return null;

  return (
    <div className="flex items-start content-center text-xs font-medium w-full  ">
      <img
        src="/static/icons/shape.svg"
        alt="Shape"
        className="inline-block size-4 mr-1"
      />
      <span className="inline-block flex-none text-neutral-50 font-medium">
        {formatDistance(distance)}
      </span>
      <span className="mx-1">·</span>
      <span className="inline-block text-neutral-50 font-medium">
        {etaMinutes} phút trở lên
      </span>
    </div>
  );
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function RatingBadge({
  rating,
  total_sold,
  className,
  isStoreDetail,
}: {
  rating: Rating;
  total_sold: number;
  className?: string;
  isStoreDetail?: boolean;
}) {
  let rawRating: number | null = null;
  const showSold = total_sold > 10;
  let soldDisplay = '';
  if (showSold) {
    soldDisplay =
      total_sold > 999 ? `${(total_sold / 1000).toFixed(1)}K` : `${total_sold}`;
  }
  if (rating?.average && rating.average > 0) {
    rawRating = rating.average;
  } else if (rating?.google_average && rating.google_average >= 4) {
    rawRating = rating.google_average;
  }

  const showRating = rawRating !== null;

  const soldElement = showSold ? (
    <span
      className="text-neutral-50 text-xs"
      data-testid="home-store-card-sold"
    >
      <img
        src="/static/icons/bag.svg"
        alt="Store"
        className="inline-block size-4 mr-1"
      />
      {soldDisplay} lượt bán
    </span>
  ) : null;

  if (!showRating) return null;

  return (
    <Badge
      variant="outline"
      className={cn(
        'flex-nowrap flex-none px-3 py-1 bg-white rounded-lg w-fit text-neutral-50',
        className
      )}
    >
      {showRating ? (
        <>
          {rating.total !== 0 ? (
            <Star
              className={cn(
                'mr-1 fill-yellow-40 text-yellow-40',
                isStoreDetail ? 'size-4' : 'size-3'
              )}
            />
          ) : (
            <img
              src="/static/images/gg-rating.webp"
              alt="Store"
              className={cn(
                'inline-block mr-1 h-auto',
                isStoreDetail ? 'w-5' : 'size-3'
              )}
            />
          )}
          <span
            className={cn(
              isStoreDetail ? 'text-primary-40 text-base' : 'text-neutral-90'
            )}
            data-testid="store-detail-rating"
          >
            {rawRating!.toFixed(1)}
          </span>{' '}
          {showSold && showRating && <span className="mx-1">|</span>}
        </>
      ) : null}
      {showSold ? (
        <span
          className="text-neutral-50 text-xs"
          data-testid="store-detail-sold"
        >
          <img
            src="/static/icons/bag.svg"
            alt="Store"
            className="inline-block size-4 mr-1"
          />
          {soldDisplay} lượt bán
        </span>
      ) : null}
    </Badge>
  );
}
export function StoreHeader({
  isOpen,
  store,
  onOpenFacebook,
  onSendMessage,
  isMobileView = false,
}: StoreHeaderProps) {
  const isMobile = useIsMobile();
  const { openSheet } = useRatingSheet();
  const cityId = useAiFoodOrderStore.use.city_id();
  const isWebinApp = useDetectWebInApp();
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const isValidPageId = useCallback((pageId: number) => {
    return pageId && pageId > 0;
  }, []);

  const openFacebookOnDesktop = useCallback((pageId: number) => {
    window.open(`https://www.facebook.com/${pageId}`, '_blank');
  }, []);

  const handleOpenFacebook = useCallback(
    (pageId: number) => {
      if (!isValidPageId(pageId)) {
        toast.error('Không thể mở fanpage của cửa hàng này, vui lòng thử lại.');
        return;
      }

      if (isMobile && isWebinApp) {
        const deeplink = `fb://page/${pageId}`;
        const fallback = `https://www.facebook.com/${pageId}`;

        JSFunction.call({
          name: 'openPage',
          body: {
            url: deeplink,
            fallback,
            type: 'external_app',
          },
        });
      } else if (isMobile) {
        const deeplink = `fb://page/${pageId}`;
        const fallback = `https://www.facebook.com/${pageId}`;

        const tempLink = document.createElement('a');
        tempLink.href = deeplink;
        tempLink.click();

        setTimeout(() => {
          window.open(fallback, '_blank');
        }, 1000);
      } else {
        openFacebookOnDesktop(pageId);
      }
    },
    [isMobile, isValidPageId, isWebinApp, openFacebookOnDesktop]
  );

  if (isMobileView) {
    return (
      <div className="p-4 bg-white pt-0 flex flex-col gap-4 w-full">
        <div className="flex items-center justify-between [&_svg]:size-5 [&_svg]:text-neutral-70 gap-4">
          <h1
            className="text-2xl font-bold text-[#1a1a1a] line-clamp-2"
            data-testid="store-detail-name"
          >
            {store?.name ?? 'Cửa hàng'}
          </h1>
          <button onClick={() => setIsSheetOpen(true)} className="p-1">
            <ChevronRight />
          </button>
        </div>

        <div className="flex flex-row w-full items-center gap-2">
          {store?.rating && store.total_sold ? (
            <Button
              onClick={() => openSheet(store)}
              variant="ghost"
              className="p-0 h-auto min-w-fit"
            >
              <RatingBadge
                isStoreDetail
                rating={store?.rating}
                total_sold={store?.total_sold}
              />
            </Button>
          ) : null}
          {store?.distance > 0 && (
            <ETABadge cityId={cityId} distance={store.distance} />
          )}
        </div>
        {store.page_id && store.page_id !== 0 ? (
          <Button
            onClick={() => onSendMessage(store?.page_id ?? 0)}
            className="bg-primary-10 text-primary-40 w-full font-bold text-base"
            variant="secondary"
            data-testid="store-detail-chat-button"
          >
            <img
              src="/static/icons/messenger.svg"
              alt="Messenger"
              className="inline-block size-6 mr-1"
            />
            Chat với cửa hàng
          </Button>
        ) : null}

        <div className="flex items-start content-center text-sm font-normal w-full">
          {isOpen ? (
            <span className="text-green-50 flex-none font-medium">Mở cửa</span>
          ) : (
            <span className="text-red-50 flex-none font-medium">Đóng cửa</span>
          )}
          <MapPin className="size-3 mr-1 ml-2 text-neutral-50 shrink-0 mt-0.5" />
          <span className="text-neutral-50 line-clamp-1">
            {store?.nearest_branch?.address ?? store?.address}
          </span>
        </div>

        <Separator className="w-full h-px" />

        {/* Badges */}
        {store?.ai_tags?.length > 0 && (
          <div className="flex flex-col gap-4">
            <h2 className="font-bold">Ưu đãi cửa hàng</h2>
            <div className="flex whitespace-nowrap overflow-x-auto gap-2 scrollbar-hide">
              {store?.ai_tags?.map((tag, index) => (
                <Badge
                  key={index}
                  className="flex-nowrap rounded-lg border-yellow-30 bg-white hover:bg-white font-medium text-sm capitalize text-neutral-90 py-1"
                >
                  <PromotionStar className="mr-1 size-5" />
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Store Detail Sheet */}
        <StoreDetailSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          store={store}
          onOpenFacebook={onOpenFacebook}
        />
      </div>
    );
  }

  // Desktop view
  return (
    <div className="mb-8">
      <div className="flex flex-row gap-6 mb-8">
        {/* Hero Image */}
        <div className="relative h-64 xl:h-80 overflow-hidden rounded-md flex-1">
          <Image
            src={
              store?.image_url && store.image_url.trim() !== ''
                ? store.image_url
                : `${assetPrefix}/static/images/empty-store.webp`
            }
            alt={store?.name ?? 'Store image'}
            fill
            className="object-cover"
            priority
            data-testid="store-detail-image"
          />
        </div>

        {/* Restaurant Info */}
        <div className="flex-1">
          <h1 className="text-2xl xl:text-3xl font-bold text-[#1a1a1a] mb-4">
            {store?.name ?? 'Store Name'}
          </h1>

          <div className="flex items-start content-center text-base font-medium mb-6 w-full">
            <MapPin className="size-5 mr-1 text-brand-primary shrink-0 mt-0.5" />
            <span className="text-neutral-50 ml-2 leading-relaxed block">
              {store?.distance > 0 && (
                <span className="inline-block flex-none text-brand-primary mr-2 font-medium">
                  {formatDistance(store.distance)}
                </span>
              )}
              <span>{store?.address}</span>
            </span>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-6">
            {store?.ai_tags?.map((tag, index) => (
              <Badge
                key={index}
                className="flex-nowrap border-yellow-30 px-3 py-1 bg-white text-base font-semibold capitalize text-yellow-30"
              >
                <PromotionStar className="mr-1 size-5" />
                {tag}
              </Badge>
            ))}
          </div>

          {/* Action Buttons */}
          {store.page_id && store.page_id !== 0 ? (
            <div className="flex flex-row gap-4 mb-6 w-full">
              <Button
                className="flex-1 border border-blue-50 text-blue-50 hover:bg-blue-50 hover:text-white font-bold rounded-sm py-3 text-base"
                onClick={() => handleOpenFacebook(store?.page_id ?? 0)}
                variant="outline"
              >
                <Facebook className="size-5" />
                Xem fanpage
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-yellow-40 to-primary-50 text-white font-bold rounded-sm py-3 text-base"
                onClick={() => onSendMessage(store?.page_id ?? 0)}
                variant="default"
              >
                Chat đặt món ngay
              </Button>
            </div>
          ) : null}

          <div className="flex items-start content-center text-sm font-normal w-full">
            {isOpen ? (
              <span className="text-green-50 flex-none">Mở cửa</span>
            ) : (
              <span className="text-red-50 flex-none">Đóng cửa</span>
            )}
            <MapPin className="size-3 mr-1 ml-2 text-neutral-50 shrink-0 mt-0.5" />
            <span className="text-neutral-50 line-clamp-1">
              {store?.nearest_branch?.address ?? store?.address}
            </span>
          </div>

          <div className="flex items-start content-center text-sm font-normal w-full">
            <ETABadge cityId={store?.city_id} distance={store?.distance} />
          </div>
        </div>
      </div>

      {/* Store Detail Sheet */}
      <StoreDetailSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        store={store}
        onOpenFacebook={onOpenFacebook}
      />
    </div>
  );
}
